
import * as jose from "jose"

const DEFAULT_JWT_AUDIENCE = 'backstage'
const WITBOOST_ENV_PLACEHOLDER = 'CHANGEME'

const getCertifiedIssuer = (): string => {
  // Read window['env'] at call time (not at import time) so that
  // dynamically loaded env.js is always picked up correctly
  const windowEnv = (typeof window !== 'undefined' && (window as any)['env']) || {}
  const issuer = windowEnv.REACT_APP_WB_ISSUER
  if (!issuer) {
    throw new Error("REACT_APP_WB_ISSUER is not defined")
  }
  if (issuer.includes(WITBOOST_ENV_PLACEHOLDER)) {
    throw new Error("REACT_APP_WB_ENV still contains the CHANGEME placeholder")
  }
  return issuer
}

const getExpectedAudience = (): string => {
  const windowEnv = (typeof window !== 'undefined' && (window as any)['env']) || {}
  return windowEnv.REACT_APP_JWT_AUDIENCE || DEFAULT_JWT_AUDIENCE
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message
  }
  return String(error)
}

const getDecodedJWTForDebug = (base64TokenJWT: string) => {
  try {
    return {
      header: jose.decodeProtectedHeader(base64TokenJWT),
      payload: jose.decodeJwt(base64TokenJWT),
    }
  } catch (error) {
    return {
      decodeError: getErrorMessage(error),
    }
  }
}

const assertRequiredTimestampClaims = (payload: jose.JWTPayload): void => {
  const missingClaims = ['iat', 'exp'].filter((claim) => payload[claim as keyof jose.JWTPayload] === undefined)
  if (missingClaims.length > 0) {
    throw new Error(`JWT is missing required claims: ${missingClaims.join(', ')}`)
  }
}

export async function validateJWT(base64TokenJWT: string): Promise<jose.JWTPayload> {
  const certifiedIssuer = getCertifiedIssuer()
  const expectedAudience = getExpectedAudience()

  if (!base64TokenJWT) {
    throw new Error("Invalid token: undefined or null")
  }

  const JWKS = jose.createRemoteJWKSet(new URL(`${certifiedIssuer}/.well-known/jwks.json`))
  const { payload } = await jose.jwtVerify(base64TokenJWT, JWKS, {
    issuer: certifiedIssuer,
    audience: expectedAudience,
  })

  assertRequiredTimestampClaims(payload)
  return payload
}

export async function isValidJWT(base64TokenJWT: string): Promise<boolean> {
  const certifiedIssuer = getCertifiedIssuer()
  const expectedAudience = getExpectedAudience()
  const decodedJWT = getDecodedJWTForDebug(base64TokenJWT)

  try {
    await validateJWT(base64TokenJWT)
    console.log('[Auth] JWT is valid', {
      issuer: certifiedIssuer,
      audience: expectedAudience,
      jwt: decodedJWT,
    })
    return true
  } catch (error) {
    console.warn('[Auth] JWT validation failed', {
      issuer: certifiedIssuer,
      audience: expectedAudience,
      jwt: decodedJWT,
      error: getErrorMessage(error),
    })
    return false
  }
}
